<script setup lang="ts">
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  visible: boolean;
  forceUpdate?: boolean;
  message: string;
  storeUrl?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  forceUpdate: false,
  storeUrl: null,
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'update-now': [];
  dismiss: [];
}>();

const { t } = useI18n();

const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => {
    if (!props.forceUpdate) {
      emit('update:visible', value);
    }
  },
});

const handleUpdateNow = () => {
  if (props.storeUrl) {
    // Open store URL for native apps
    window.open(props.storeUrl, '_blank');
  }
  emit('update-now');
};

const handleDismiss = () => {
  if (!props.forceUpdate) {
    emit('dismiss');
    emit('update:visible', false);
  }
};

const title = computed(() => {
  return props.forceUpdate ? t('update.required') : t('update.available');
});

// const updateButtonClass = computed(() => {
//   return props.forceUpdate
//     ? 'bg-red-600 hover:bg-red-700 text-white'
//     : 'bg-primary-600 hover:bg-primary-700 text-white';
// });
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :closable="!forceUpdate"
    :close-on-escape="!forceUpdate"
    :dismissable-mask="!forceUpdate"
    :draggable="false"
    :header="title"
    :style="{ width: '90vw', maxWidth: '450px' }"
  >
    <!-- Message Content -->
    <div class="space-y-4">
      <p class="leading-relaxed text-gray-700">
        {{ message }}
      </p>

      <div v-if="forceUpdate" class="flex items-start gap-3 rounded-lg bg-red-50 p-4">
        <i class="pi pi-exclamation-triangle mt-1 text-red-600" />
        <div class="text-sm text-red-800">
          <p class="font-medium">{{ t('update.forceUpdateWarning') }}</p>
          <p class="mt-1">{{ t('update.forceUpdateMessage') }}</p>
        </div>
      </div>

      <div v-else class="flex items-start gap-3 rounded-lg bg-blue-50 p-4">
        <i class="pi pi-info-circle mt-1 text-blue-600" />
        <div class="text-sm text-blue-800">
          <p>{{ t('update.optionalUpdateMessage') }}</p>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <template #footer>
      <div class="flex w-full flex-row gap-2">
        <Button :label="t('update.updateNow')" class="w-full" @click="handleUpdateNow" />
        <Button v-if="!forceUpdate" :label="t('common.later')" class="w-full" severity="secondary" outlined @click="handleDismiss" />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Additional styles if needed */
</style>
